import store from '@/store/store'
import { NavigationGuardNext, RouteLocation } from 'vue-router'
import { Store } from 'vuex'
import { HexaplaState } from '@/store/state'
import { isAdmin } from '@/hexapla-core/domains/accounts/roles'

interface Guard {
  (to: RouteLocation, from: RouteLocation, next: NavigationGuardNext): void
}

export interface NavGuards {
  privateRoute: Guard
  adminOnly: Guard
  supervisorOnly: Guard
  authed: Guard
}

export function privateRoute(to: RouteLocation, from: RouteLocation, next: NavigationGuardNext) {
  if (!(store as Store<HexaplaState>).state.user.isLoggedIn) {
    return next('/login')
  } else next()
}

// The admin role list used to be spelled out here as well as in the API's
// Plugs.Authenticated. It now lives once, in domains/accounts/roles.
export function adminOnly(to: RouteLocation, from: RouteLocation, next: NavigationGuardNext) {
  const { roles } = (store as Store<HexaplaState>).state.user
  if (isAdmin(roles)) {
    return next()
  } else next(from.path)
}

/**
 * Guards the review queue.
 *
 * Supervision is a relationship, not a role: anyone with at least one fellow
 * reporting to them qualifies. Admins pass too, since they can act on any
 * queue. This only hides the route — the API returns an empty queue to anyone
 * who supervises nobody, so a hand-typed URL leaks nothing.
 */
export function supervisorOnly(to: RouteLocation, from: RouteLocation, next: NavigationGuardNext) {
  const { roles, isSupervisor } = (store as Store<HexaplaState>).state.user
  if (isAdmin(roles) || isSupervisor) {
    return next()
  } else next(from.path)
}

export function authed(store: Store<HexaplaState>) {
  return function (to: RouteLocation, from: RouteLocation, next: NavigationGuardNext) {
    if (!store.state.user.isLoggedIn) {
      return next('/login')
    } else next()
  }
}

export default {
  privateRoute,
  adminOnly,
  supervisorOnly,
  authed: authed(store as Store<HexaplaState>),
} as NavGuards
